import { useEffect, useMemo, useState } from 'react'

import {
  IconButton,
  IconMagnifyingGlass,
  Search,
  Tooltip
} from '@vtex/shoreline'
import { debounce } from 'lodash'

import useQueryParams from '~/hooks/useQuery'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import { useCollapsibleField } from './useCollapsibleField'
import { useHotkey } from './useHotkey'
import { usePushSearchParams } from './usePushSearchParams'

import styles from './styles.module.css'

const DEBOUNCE = 700

/**
 * Ícone que expande no clique ou no atalho `/`, com debounce e estado
 * persistido na URL. Recolhe ao sair do campo sem termo digitado.
 */
export const SearchFilter: React.FC<{ placeholder: string }> = ({
  placeholder
}) => {
  const { t } = useAPPTranslation()
  const params = useQueryParams()
  const pushParams = usePushSearchParams()
  const urlQuery = params.get('query') || ''

  const [value, setValue] = useState(urlQuery)
  const [isDebouncing, setIsDebouncing] = useState(false)
  const { fieldRef, isClosing, isExpanded, collapse, open } =
    useCollapsibleField(!!value)

  // navegação externa (deep link, voltar) reflete no campo
  useEffect(() => setValue(urlQuery), [urlQuery])

  const push = useMemo(
    () =>
      debounce((next: string) => {
        pushParams(nextParams => {
          if (next) nextParams.set('query', next)
          else nextParams.delete('query')

          nextParams.set('page', '1')
        })
        setIsDebouncing(false)
      }, DEBOUNCE),
    [pushParams]
  )

  const handleChange = (next: string) => {
    setValue(next)
    setIsDebouncing(true)
    push(next)
  }

  // nada pode navegar depois do unmount
  useEffect(() => () => push.cancel(), [push])

  useHotkey(event => event.key === '/', open)

  const label = `${t('rm:advertiserCatalog.search')} (/)`

  if (!isExpanded && !isClosing) {
    return (
      <Tooltip label={label}>
        <IconButton variant="secondary" label={label} onClick={open}>
          <IconMagnifyingGlass />
        </IconButton>
      </Tooltip>
    )
  }

  return (
    <div
      ref={fieldRef}
      className={`${styles.headerSearch} ${
        isClosing ? styles.headerSearchClosing : ''
      }`}
    >
      <Search
        value={value}
        loading={isDebouncing}
        messages={{ placeholder }}
        onChange={event => handleChange(event.target.value)}
        onBlur={() => {
          if (!value) collapse()
        }}
        onClear={() => {
          handleChange('')
          collapse()
        }}
      />
    </div>
  )
}
